/**
 * compliance-brief.ts — Editorial copy layer for the Baseline Compliance
 * pillar standfirst.
 *
 * Mirrors `posture-brief.ts` exactly in shape, seeding, and voice rules.
 * The subject is "Baseline" — the brief describes the compliance pillar
 * score and its 30-day movement, NOT the count of new drifts opened
 * (that lives in Section · 01 on the page, driven by `summarizeDriftTrend`).
 * Keeping two distinct nouns ("baseline" for score-movement copy, "drift"
 * for queue-volume copy) is the editorial fix for the user-visible
 * contradiction where the brief said "Drift is settled" while the chart
 * underneath said "Drift is climbing" — different stats, same word.
 *
 * Every slot routes off the shared `bucketDelta` direction so the modifier,
 * verdict, weak-link, deck-trend, and deck-action never disagree on whether
 * things are improving, holding, or slipping.
 *
 * Variant selection is deterministic from a `${tenantId}:${localDay}:${slot}`
 * seed so the same tenant sees a stable brief through the day. Verdicts
 * cap at six words to fit the ScoreBox quote.
 */

import {
  cap,
  formatKicker,
  localDayKey,
  pickVariant,
  bucketDelta,
  isNegative,
  isNonNegative,
  type DeltaBucket,
} from './brief-helpers'

export type Pillar = 'compliance' | 'bpa' | 'identity'

export interface ComplianceBrief {
  /** Pill above the headline: "Today's Brief · 29 April" */
  kicker: string
  /** First word(s) of headline rendered roman, e.g. "Baseline is" */
  headlineSubject: string
  /** Italic emphasis word, e.g. "settled" — gets `<em>` treatment */
  headlineModifier: string
  /** Second sentence on the headline line. Complete, capitalized, period-terminated. */
  weakLink: string
  /** Single-paragraph deck under the headline. Uses `**bold**` markers. */
  deck: string
  /** Six-words-or-fewer verdict in the score box, italic, in quotes */
  verdict: string
  /** Byline footer */
  byline: string
  /** Score delta vs N days ago, drives the green pill */
  delta: { value: number; days: number }
}

export interface ComplianceBriefInput {
  /** Currently signed-in user email, e.g. "adam.jones@iuvotech.com" */
  userEmail?: string
  /**
   * Active tenant ID. Combined with the local day to seed variant selection
   * so the brief is stable per tenant per day.
   */
  tenantId?: string
  /** Compliance pillar score (0-100). */
  score?: number | null
  /** 30-day score delta — drives modifier choice and the score-box pill. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact priority drift / action — informs the verdict + deck risk slot. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity drift count — drives the weak-link clause. */
  criticalCount?: number
  /**
   * Canonical workload names with active drifts — e.g. "Identity",
   * "SharePoint". Map from M365DSC component prefixes via
   * `getWorkloadForDriftComponent`; do not pass resource names or M365DSC
   * resource codes here. The composer surfaces names when 1-2 workloads
   * carry the drift; 3+ falls through to count-free "spread" phrasing,
   * because a workload is a named subject, not a unit you tally.
   */
  driftingWorkloads?: string[]
  /** Override "today" — primarily for testing the kicker. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const PILLAR_LABEL: Record<Pillar, string> = {
  compliance: 'Compliance',
  bpa: 'Best Practice',
  identity: 'Identity',
}

/**
 * Single-word pillar names — used inside the six-word verdict cap so
 * "Best Practice" doesn't blow past the budget.
 */
const PILLAR_LABEL_SHORT: Record<Pillar, string> = {
  compliance: 'Compliance',
  bpa: 'Practice',
  identity: 'Identity',
}

function imperativeFromAction(title?: string): string {
  if (!title) return 'triage'
  const first = title.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '') ?? ''
  const known = new Set([
    'enforce',
    'roll',
    'reclaim',
    'review',
    'remove',
    'rotate',
    'restrict',
    'require',
    'rebuild',
    'remediate',
    'audit',
    'patch',
    'disable',
    'block',
    'tighten',
    'triage',
    'address',
    'commit',
    'approve',
    'revert',
    'reconcile',
  ])
  return known.has(first) ? first : 'triage'
}

const NUMBER_WORD_CAP: readonly string[] = [
  'zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
]

const NUMBER_WORD_LOWER: readonly string[] = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
]

function numberWord(n: number, capitalized: boolean): string {
  const table = capitalized ? NUMBER_WORD_CAP : NUMBER_WORD_LOWER
  return table[n] ?? String(n)
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the ScoreBox cap)                 */
/* -------------------------------------------------------------------------- */

const VERDICT_SETTLED: readonly string[] = [
  'Baseline is settled.',
  'Baseline holds. Routine watch only.',
  'Settled. Eyes on it only.',
  'Baseline is quiet today.',
]

const VERDICT_IMPROVING: readonly string[] = [
  'Baseline is lifting.',
  'Improving. Keep at it.',
  'Trending the right way.',
  'Lifting. Keep it steady.',
]

/** High score + slipping: state is still good, but trend says it's softening.
 *  Used at score ≥85 when delta < 0 — the gap codex flagged: previously
 *  "settled" fired here, contradicting the deck trend. */
const VERDICT_HIGH_SLIPPING: readonly string[] = [
  'Strong, but softening.',
  'Baseline is slipping.',
  'High base, easing back.',
]

/** Templates take an `{n}` token (lowercase number word: "two" / "three"). */
const VERDICT_MIXED_COUNT: readonly string[] = [
  'Mixed. {n} drifts to tidy up.',
  'Steady. {n} drifts still open.',
  'Holding. {n} drifts to close.',
]

const VERDICT_MIXED_BARE: readonly string[] = [
  'Mixed picture. Pick a starting point.',
  'Steady but soft. Pick first move.',
  'Mixed. Take the lead drift first.',
]

/** Mid-band + slipping: score in 70-84, delta < 0. New pool — used to be
 *  silently routed through MIXED_BARE / MIXED_COUNT which read as "everything
 *  is fine, just busy" rather than naming the decline. */
const VERDICT_MID_SLIPPING: readonly string[] = [
  'Slipping. Pick first move.',
  'Softening. Take the lead drift.',
  'Heading the wrong way.',
]

/** Templates take a `{verb}` token (capitalized imperative). */
const VERDICT_DRIFTING: readonly string[] = [
  'Drifting fast. {verb} first.',
  'Drift spreading. {verb} now.',
  'Slipping. {verb} the lead drift.',
]

/** Templates take a `{pillar}` token (short pillar name). */
const VERDICT_UNSETTLED: readonly string[] = [
  'Drift is unsettled. Start with {pillar}.',
  'Unsettled. Begin with {pillar}.',
  'Soft baseline. Lead with {pillar}.',
]

type WeakWorkload = { name: string }

interface VerdictContext {
  score: number
  bucket: DeltaBucket
  criticalCount: number
  topAction: ComplianceBriefInput['topAction']
}

function pickVerdict(ctx: VerdictContext, seed: string): string {
  const { score, bucket, criticalCount, topAction } = ctx

  // ≥85 — settled (flat), improving (rising), softening (slipping/falling).
  // The previous logic routed every non-positive delta to SETTLED, which is
  // how "Drift is settled" ended up paired with "Drift has accelerated" in
  // the same brief. Direction is now honest at every band.
  if (score >= 85) {
    if (bucket === 'risingFast' || bucket === 'rising') {
      return pickVariant(VERDICT_IMPROVING, seed, 'verdict.improving.high')
    }
    if (isNegative(bucket)) {
      return pickVariant(VERDICT_HIGH_SLIPPING, seed, 'verdict.highSlipping')
    }
    return pickVariant(VERDICT_SETTLED, seed, 'verdict.settled')
  }

  // 70-84 — improving on rising delta, slipping on falling, otherwise mixed.
  // The verdict is count-driven (or bare) — workload names go in the
  // weak-link clause, never the verdict slot.
  if (score >= 70) {
    if (bucket === 'risingFast' || bucket === 'rising') {
      return pickVariant(VERDICT_IMPROVING, seed, 'verdict.improving.mid')
    }
    if (isNegative(bucket)) {
      return pickVariant(VERDICT_MID_SLIPPING, seed, 'verdict.midSlipping')
    }
    if (criticalCount > 0 && criticalCount <= 6) {
      const tpl = pickVariant(VERDICT_MIXED_COUNT, seed, 'verdict.mixed.count')
      return tpl.replace('{n}', numberWord(criticalCount, false))
    }
    return pickVariant(VERDICT_MIXED_BARE, seed, 'verdict.mixed.bare')
  }

  // 55-69 — recovering when delta is positive (still a soft score, but
  // moving the right way), mixed when truly flat, drifting when negative.
  if (score >= 55) {
    if (bucket === 'risingFast' || bucket === 'rising') {
      return pickVariant(VERDICT_IMPROVING, seed, 'verdict.improving.low')
    }
    if (bucket === 'flat') {
      return pickVariant(VERDICT_MIXED_BARE, seed, 'verdict.mixed.lowBare')
    }
    const verb = cap(imperativeFromAction(topAction?.title))
    const tpl = pickVariant(VERDICT_DRIFTING, seed, 'verdict.drifting')
    return tpl.replace('{verb}', verb)
  }

  // <55 — unsettled; point at the pillar implied by the top action
  const pillarShort = topAction?.pillar
    ? PILLAR_LABEL_SHORT[topAction.pillar]
    : PILLAR_LABEL_SHORT.identity
  const tpl = pickVariant(VERDICT_UNSETTLED, seed, 'verdict.unsettled')
  return tpl.replace('{pillar}', pillarShort)
}

/* -------------------------------------------------------------------------- */
/* Headline modifier (italic emphasis word — kept as single-word table)       */
/* -------------------------------------------------------------------------- */

/**
 * Modifier is delta-aware at every band — the previous "55-69 always reads
 * weakening regardless of delta" rule contradicted the deck trend sentence
 * (which already routed off delta), so the headline could say
 * "Baseline is weakening" while the deck said "no meaningful shift". Same
 * fix at every other band.
 */
function pickModifier(score: number, bucket: DeltaBucket): string {
  if (score >= 85) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'improving'
    if (isNegative(bucket)) return 'slipping'
    return 'settled'
  }
  if (score >= 70) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'improving'
    if (isNegative(bucket)) return 'slipping'
    return 'steady'
  }
  if (score >= 55) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'recovering'
    if (isNegative(bucket)) return 'drifting'
    return 'mixed'
  }
  if (bucket === 'risingFast' || bucket === 'rising') return 'recovering'
  return 'unsettled'
}

/* -------------------------------------------------------------------------- */
/* Weak-link sentence (complete sentence, no leading dash)                    */
/* -------------------------------------------------------------------------- */

const WEAKLINK_CLEAR: readonly string[] = [
  'Baseline is steady across the board.',
  'No workload is drifting today.',
  "Nothing's asking for your time right now.",
]

const WEAKLINK_ONE_COUNT: readonly string[] = [
  'One drift to tidy up.',
  'A single drift stands out.',
  'One soft spot is dragging the score.',
]

/** Templates take an `{n}` token (capitalized: "Two" / "Three"). */
const WEAKLINK_FEW_COUNT: readonly string[] = [
  '{n} drifts to tidy up.',
  '{n} items are dragging the score.',
  '{n} soft spots stand out.',
]

/** Heavy drift volume AND the baseline is actually moving the wrong way.
 *  Reserved for the case codex's rubric calls out — "stacking up" reads as
 *  worsening, so it can only fire when the trend bucket actually says so. */
const WEAKLINK_MANY_DECLINING: readonly string[] = [
  'Drift is spreading across pillars.',
  'The baseline is heading the wrong way.',
  'Several drifts are stacking up across workloads.',
]

/** Heavy drift volume but baseline is steady or improving — describe the
 *  state without implying motion downward. This is the slot previously
 *  conflated with WEAKLINK_MANY_DECLINING, which is what made "Drift is
 *  settled" pair with "Several drifts are stacking up". */
const WEAKLINK_MANY_NEUTRAL: readonly string[] = [
  'Drift is spread across workloads.',
  'Several workloads still need a hand.',
  'A few workloads carry the drift today.',
]

/** Templates take a `{workload}` token (single workload label). */
const WEAKLINK_ONE_WORKLOAD: readonly string[] = [
  '{workload} still needs a hand.',
  '{workload} is the one workload drifting today.',
  '{workload} is dragging the score.',
]

/** Templates take a `{workload}` token. Used for two named workloads. */
const WEAKLINK_TWO_WORKLOADS: readonly string[] = [
  '{workload} still need a hand.',
  '{workload} are drifting today.',
  '{workload} are dragging the score.',
]

function joinTwo(a: string, b: string): string {
  return `${a} and ${b}`
}

function buildWeakLink(
  criticalCount: number | undefined,
  workloads: WeakWorkload[],
  bucket: DeltaBucket,
  seed: string,
): string {
  // One or two workloads — name them. This is the editorial preference and
  // the whole point of the weak-link clause: a thoughtful colleague would
  // say "Identity and SharePoint are drifting today", not "Two workloads".
  if (workloads.length === 1) {
    const tpl = pickVariant(WEAKLINK_ONE_WORKLOAD, seed, 'weakLink.oneWorkload')
    return tpl.replace('{workload}', workloads[0].name)
  }
  if (workloads.length === 2) {
    const tpl = pickVariant(WEAKLINK_TWO_WORKLOADS, seed, 'weakLink.twoWorkloads')
    return tpl.replace('{workload}', joinTwo(workloads[0].name, workloads[1].name))
  }

  // Three or more workloads — split by trend so the language matches reality.
  // Worsening trend earns "stacking up" / "heading the wrong way"; a stable
  // baseline with 3+ workloads gets the neutral pool.
  if (workloads.length >= 3) {
    if (isNegative(bucket)) {
      return pickVariant(WEAKLINK_MANY_DECLINING, seed, 'weakLink.manyDeclining')
    }
    return pickVariant(WEAKLINK_MANY_NEUTRAL, seed, 'weakLink.manyNeutral')
  }

  // No workload data — fall back to drift-count phrases.
  if (typeof criticalCount === 'number' && criticalCount > 0) {
    if (criticalCount === 1) return pickVariant(WEAKLINK_ONE_COUNT, seed, 'weakLink.oneCount')
    if (criticalCount <= 6) {
      const tpl = pickVariant(WEAKLINK_FEW_COUNT, seed, 'weakLink.fewCount')
      return tpl.replace('{n}', numberWord(criticalCount, true))
    }
    if (isNegative(bucket)) {
      return pickVariant(WEAKLINK_MANY_DECLINING, seed, 'weakLink.manyDeclining')
    }
    return pickVariant(WEAKLINK_MANY_NEUTRAL, seed, 'weakLink.manyNeutral')
  }

  return pickVariant(WEAKLINK_CLEAR, seed, 'weakLink.clear')
}

/* -------------------------------------------------------------------------- */
/* Deck (trend + risk + action, em-dash free)                                 */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING_FAST: readonly string[] = [
  'The baseline score has lifted sharply over the past 30 days.',
  'The baseline has tightened up nicely across the past month.',
  'Baseline score is well up on a month ago.',
]

const TREND_LIFTING: readonly string[] = [
  'The baseline score has ticked up a little over the past 30 days.',
  'The baseline has improved over the past month.',
  'Small baseline gains over the past 30 days, spread across workloads.',
]

const TREND_FLAT: readonly string[] = [
  "The baseline has held flat over the past 30 days. You're not losing ground, but you're not gaining either.",
  "The baseline score hasn't moved over the past 30 days.",
  'The baseline has been steady for a month, with no real shift either way.',
]

const TREND_SLIPPING: readonly string[] = [
  'The baseline has slipped a touch over the past 30 days. Nothing dramatic, but worth a look.',
  'The baseline score is down a touch over the past month, worth a look when you can.',
  "A modest decline in the baseline over the past 30 days. Not yet a trend, but it's one to keep an eye on.",
]

const TREND_FALLING: readonly string[] = [
  'The baseline has dropped sharply over the past 30 days and needs eyes on it this week.',
  'The baseline has fallen meaningfully over the past month.',
  "A significant decline in the baseline across the past 30 days. This one needs a real response, not next sprint.",
]

/** Templates take a `{phrase}` token (already-bolded action title). */
const RISK_TOP_ACTION: readonly string[] = [
  '{phrase} is your largest open drift and the one move that lifts the score most.',
  '{phrase} is the biggest move you can make on the board today.',
  '{phrase} is where your time pays back fastest.',
]

/** Templates take a `{workload}` token (already-bolded workload name). */
const RISK_WORKLOAD: readonly string[] = [
  '{workload} is the workload dragging the baseline down, and the one to chase first.',
  "{workload} is the lagging workload; that's where your time pays back fastest.",
  '{workload} is dragging the baseline down today.',
]

const RISK_HYGIENE: readonly string[] = [
  "Nothing's shouting for attention. What's left is hygiene work.",
  'The rest is hygiene rather than headline drift.',
  "No single gap is leading the story. It's housekeeping from here.",
]

const RISK_COMPOUND: readonly string[] = [
  'Several smaller drifts add up; closing the biggest of them lifts the baseline fastest.',
  'A handful of smaller drifts stack up. The biggest is the place to start.',
  "A handful of smaller drifts add up. The biggest is the one to chase first.",
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing and run the usual review cadence.",
  "You're on the right track. Keep the routine reviews running.",
  'Stay the course this week.',
]

const ACTION_LEAD: readonly string[] = [
  'Take the lead drift this week to keep the momentum going.',
  'Take the lead drift this week.',
  'Knock out the lead drift this week so the momentum holds.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Take the highest-impact drifts first; the rest can wait for next sprint.',
  "Focus on the highest-impact drifts first; the rest holds until you've got room.",
  "Take the high-impact drifts first and let the rest sit for now.",
]

const ACTION_PUSH: readonly string[] = [
  'This one needs a real week of focus, not the usual cadence.',
  'Plan a focused remediation push this sprint.',
  "This is a remediation sprint, not routine work.",
]

function buildDeck(
  score: number,
  bucket: DeltaBucket,
  workloads: WeakWorkload[],
  criticalCount: number,
  topAction: ComplianceBriefInput['topAction'] | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (bucket === 'risingFast') return pickVariant(TREND_LIFTING_FAST, seed, 'deck.trend.liftingFast')
    if (bucket === 'rising') return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (bucket === 'flat') return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (bucket === 'slipping') return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (topAction?.title) {
      const phrase = `**${topAction.title.replace(/[.,;:!?]+$/, '')}**`
      const tpl = pickVariant(RISK_TOP_ACTION, seed, 'deck.risk.topAction')
      return tpl.replace('{phrase}', phrase)
    }
    if (workloads.length > 0 && score < 85) {
      const phrase = `**${workloads[0].name}**`
      const tpl = pickVariant(RISK_WORKLOAD, seed, 'deck.risk.workload')
      return tpl.replace('{workload}', phrase)
    }
    if (score >= 85) return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    return pickVariant(RISK_COMPOUND, seed, 'deck.risk.compound')
  })()

  // Action routing is now trend-first: a falling baseline always earns PUSH
  // and a slipping one always earns LEAD, regardless of score band. HOLD only
  // fires when the score is genuinely high AND non-negative AND no broad
  // workload pressure — the previous "score >= 90 → HOLD" rule paired
  // "stick with the existing review cadence" with "needs eyes on it this
  // week" in the same paragraph.
  const action = (() => {
    if (bucket === 'falling') return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
    if (bucket === 'slipping') return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    const calm =
      score >= 90 && isNonNegative(bucket) && workloads.length < 3 && criticalCount <= 3
    if (calm) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (score >= 75) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (score >= 60) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a ComplianceBrief from the active Compliance pillar state. All
 * inputs are optional — the function degrades gracefully toward a plausible
 * "no data" brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band "unsettled" verdict. Without this guard the
 * composer would coerce `null → 0`, which lands the loading state in the
 * lowest editorial band and shows the user an authoritative-but-wrong
 * verdict for a fraction of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value and still routes through
 * the normal band logic — only `null`/`undefined` triggers the loading brief.
 */
export function composeComplianceBrief(input: ComplianceBriefInput = {}): ComplianceBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.score == null) {
    return {
      kicker: formatKicker(now),
      headlineSubject: 'Baseline is',
      headlineModifier: 'loading',
      weakLink: 'Reading the latest baseline.',
      deck: 'Pulling the latest baseline data. The brief updates once the scan resolves.',
      verdict: 'Reading baseline…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
    }
  }

  const score = input.score
  const bucket = bucketDelta(deltaValue)

  const workloads: WeakWorkload[] = (input.driftingWorkloads ?? [])
    .filter((w): w is string => typeof w === 'string' && w.trim().length > 0)
    .map(w => ({ name: w.trim() }))

  // Daily seed: stable per (tenant, local day).
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  const headlineModifier = pickModifier(score, bucket)
  const weakLink = buildWeakLink(input.criticalCount, workloads, bucket, seed)
  const verdict = pickVerdict(
    {
      score,
      bucket,
      criticalCount: input.criticalCount ?? 0,
      topAction: input.topAction,
    },
    seed,
  )
  const deck = buildDeck(score, bucket, workloads, input.criticalCount ?? 0, input.topAction, seed)

  return {
    kicker: formatKicker(now),
    headlineSubject: 'Baseline is',
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT_SETTLED,
  VERDICT_IMPROVING,
  VERDICT_HIGH_SLIPPING,
  VERDICT_MIXED_COUNT,
  VERDICT_MIXED_BARE,
  VERDICT_MID_SLIPPING,
  VERDICT_DRIFTING,
  VERDICT_UNSETTLED,
  WEAKLINK_CLEAR,
  WEAKLINK_ONE_COUNT,
  WEAKLINK_FEW_COUNT,
  WEAKLINK_MANY_DECLINING,
  WEAKLINK_MANY_NEUTRAL,
  WEAKLINK_ONE_WORKLOAD,
  WEAKLINK_TWO_WORKLOADS,
  PILLAR_LABEL,
  PILLAR_LABEL_SHORT,
}
